import { ListOrderNotificationsForOrderRes_ShipmentNotification } from "gen/api/order/service_pb"
import { ShipmentNotification_Product } from "gen/integrations/db/models_pb"
import { Rule_PriceStrategy, Tier } from "gen/price/models_pb"
import { OrderReceiveSettings_UpdatePrices as UpdatePrices } from "gen/vendors/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"

export interface ShipmentPriceChange {
  currentPrice: Money
  newPrice: Money
  isFixedPrice: boolean
}

// A vendor invoice can list the same product on multiple lines
// this accumulates quantities per productId
export function mergeShipmentNotificationProducts(
  products: ShipmentNotification_Product[]
): ShipmentNotification_Product[] {
  const merged: ShipmentNotification_Product[] = []
  const indexByProductId = new Map<string, number>()
  for (const product of products) {
    if (!product.productId) {
      merged.push(product)
      continue
    }
    const key = UUID.fromPB(product.productId).toString()
    const existingIndex = indexByProductId.get(key)
    if (existingIndex === undefined) {
      indexByProductId.set(key, merged.length)
      merged.push({ ...product })
      continue
    }
    const existing = merged[existingIndex]
    existing.quantityShipped += product.quantityShipped
    existing.cost = product.cost ?? existing.cost
    existing.retailPrice = product.retailPrice ?? existing.retailPrice
  }
  return merged
}

export function getShipmentPriceChanges({
  selectedShipment,
  tierPricesByProductId,
  defaultPricingTierIdString,
}: {
  selectedShipment?: ListOrderNotificationsForOrderRes_ShipmentNotification
  tierPricesByProductId?: { [id: string]: Tier[] }
  defaultPricingTierIdString?: string
}): Map<string, ShipmentPriceChange> {
  const changes = new Map<string, ShipmentPriceChange>()
  if (!defaultPricingTierIdString) {
    return changes
  }

  for (const product of mergeShipmentNotificationProducts(
    selectedShipment?.shipmentNotification?.products ?? []
  )) {
    if (!product.productId || !product.retailPrice) {
      continue
    }
    const newPrice = Money.fromDollars(
      Decimal.fromPB(product.retailPrice).toString()
    )
    if (!newPrice.isPositive()) {
      continue
    }
    const productIdString = UUID.fromPB(product.productId).toString()
    const tierPrice = tierPricesByProductId?.[productIdString]?.find(
      (tier) => tier.tierIdString === defaultPricingTierIdString
    )
    if (!tierPrice) {
      continue
    }
    const currentPrice = Money.fromPB(tierPrice.price) ?? Money.zero()
    if (newPrice.eq(currentPrice)) {
      continue
    }
    changes.set(productIdString, {
      currentPrice,
      newPrice,
      isFixedPrice: tierPrice.priceStrategy === Rule_PriceStrategy.FIXED,
    })
  }
  return changes
}

// A change only lands on receive if the tier is fixed priced and the mode covers
// it -- INCREASES_ONLY leaves a lower shipment price alone.
export function willChangeOnReceive(
  change: ShipmentPriceChange,
  updatePrices: UpdatePrices
): boolean {
  if (!change.isFixedPrice) {
    return false
  }
  if (updatePrices === UpdatePrices.ALL) {
    return true
  }
  return (
    updatePrices === UpdatePrices.INCREASES_ONLY &&
    change.newPrice.gt(change.currentPrice)
  )
}

export function getShipmentPriceChangeLabel(
  change: ShipmentPriceChange,
  updatePrices: UpdatePrices
): string {
  return willChangeOnReceive(change, updatePrices)
    ? `Price will change from ${change.currentPrice.format()} to ${change.newPrice.format()} when this shipment is received.`
    : `Shipment lists a retail price of ${change.newPrice.format()}, but the current price is ${change.currentPrice.format()}.`
}

export function getShipmentPriceChangeWarning(
  changes: Map<string, ShipmentPriceChange>,
  updatePrices: UpdatePrices
): string {
  if (changes.size === 0) {
    return ""
  }
  const baseText =
    changes.size === 1
      ? "1 product has a price change in this shipment."
      : `${changes.size} products have price changes in this shipment.`
  const willUpdateCount = [...changes.values()].filter((change) =>
    willChangeOnReceive(change, updatePrices)
  ).length
  if (willUpdateCount === 0) {
    return `${baseText} Product prices will not be updated on receive.`
  }
  if (willUpdateCount === changes.size) {
    return `${baseText} Product prices will be updated on receive.`
  }
  return `${baseText} ${willUpdateCount} of ${changes.size} will be updated on receive.`
}
